import {
  carePhaseDefs,
  formatInteger,
  formatPercent,
  formatCurrency,
  CareEnvironment,
  FundingPolicyType,
  isMembershipProgram,
  type CarePhase,
} from '@shared';

export interface PhasePayoutData {
  phase: CarePhase;
  phaseLabel: string;
  locationOfCare: string;
  phaseDuration: string;
  totalPhaseCost: number;
  avgMonthlyCost: number;
  avgDailyCost: number;

  // Policy Benefit
  policyType: FundingPolicyType | null;
  initialMaxBenefitAmount: number | null;
  policyContinuationBenefitAmount: number | null;
  inflationProtection: string | null;
  inflatedBenefitAmount: number | null;
  policyBenefitAvailable: number;

  // Eligible Care Days
  adlClaimEligibilityRequired: boolean;
  estimatedDaysInPhase: number;
  eligibleDaysWithTwoAdls: number | null;
  waitingPeriod: {
    remainingDays: number;
    totalDays: number | null;
  } | null;
  eligibleDaysAfterWaiting: number;

  // Daily Maximum Benefit
  dailyMaxBenefitForCare: number | null;
  inflatedDailyMaxBenefit: number | null;
  expectedDailyPayout: number;
  isReimbursementPolicy: boolean;

  // Cost Coverage
  remainingBenefitAmount: number;
  eligibleCareCost: number;
  maxCoverageBasedOnDailyMax: number | null;
  totalCostCoverage: number;
  remainingBenefitAfterPhase: number;
}

export interface PolicyInsightData {
  // Total Invested Into Policy
  monthlyPremium?: number | null;
  /**
   * True when the policy is billed annually (recovered from the source
   * quote's `premiumFrequency`). Drives the card to show "Annual Premium"
   * with the carrier's actual yearly bill instead of "Monthly Premium".
   */
  isAnnualBilled: boolean;
  /**
   * Carrier-quoted annual premium when available — preserves the exact
   * carrier price (e.g. $797/yr) rather than the lossy `monthly × 12` round
   * trip. Falls back to `monthly × 12` when sourceQuote is missing or the
   * user has edited the policy since import.
   */
  annualPremium?: number | null;
  policyDuration: string;
  monthlyPremiumTotalCost?: number | null;
  lumpSumPayment?: number | null;
  predictedTotalPremiumsPaid?: number | null;
  isLumpSumOnly: boolean;

  // Long-Term Care Payout Summary
  earlyCarePhase: number | null;
  moderateCarePhase: number | null;
  fullCarePhase: number | null;
  totalPolicyCoverage: number | null;

  // Phase-specific calculations
  phases: PhasePayoutData[];
}

export function buildPhasePayoutData(
  client: Client,
  phaseCalculations: SinglePhaseCalculations[],
  ltcPolicy: PolicyFundingSource,
): PhasePayoutData[] {
  const { phaseCosts, appliedCareEnvironments, intakeSurvey } = client;
  const { clientCurrentCarePhase, clientAdlReceivedAssistanceCount } = intakeSurvey;

  return phaseCosts
    .filter(phaseCost => {
      if (!clientCurrentCarePhase) return true;
      return (
        carePhaseDefs[phaseCost.carePhase].index >= carePhaseDefs[clientCurrentCarePhase].index
      );
    })
    .map((phaseCost, index) => {
      const carePhase = phaseCost.carePhase;
      const phaseCalc = phaseCalculations[carePhaseDefs[carePhase].index];
      const careEnvironment = appliedCareEnvironments?.[carePhase] as CareEnvironment;

      const isEarlyCarePhase = carePhaseDefs[carePhase].index === 0;
      const hasLessThanTwoAdls = clientAdlReceivedAssistanceCount < 2;
      const adlClaimEligibilityRequired =
        isEarlyCarePhase && hasLessThanTwoAdls && !isMembershipProgram(ltcPolicy.policyType);

      const waitingPeriodDays =
        careEnvironment === CareEnvironment.home
          ? ltcPolicy.homeCareWaitingPeriodDays
          : ltcPolicy.facilityCareWaitingPeriodDays;

      // Use phase-specific remaining waiting period days based on care environment
      const phaseWaitingPeriodDaysRemaining =
        careEnvironment === CareEnvironment.home
          ? phaseCalc.phaseHomeCareWaitingPeriodDaysRemaining
          : phaseCalc.phaseFacilityCareWaitingPeriodDaysRemaining;

      const waitingPeriod =
        waitingPeriodDays !== null && waitingPeriodDays > 0
          ? {
              remainingDays:
                phaseWaitingPeriodDaysRemaining ?? phaseCalc.applicableWaitingPeriodDays,
              totalDays: waitingPeriodDays,
            }
          : null;

      const expectedDailyPayout = ltcPolicy.isIndemnityPolicyPayment
        ? phaseCalc.inflatedDailyBenefitAmount
        : Math.min(phaseCalc.inflatedDailyBenefitAmount, phaseCalc.phaseCostPerDayNeeded);

      return {
        phase: carePhase,
        phaseLabel: carePhaseDefs[carePhase].label,
        locationOfCare: getCareEnvironmentLabel(careEnvironment),
        phaseDuration: `${formatInteger(phaseCost.phaseCareMonthsNeeded)} months (${formatInteger(phaseCalc.phaseCareDaysNeeded)} days)`,
        totalPhaseCost: phaseCost.phaseInflatedProfessionalShareCost,
        avgMonthlyCost: phaseCalc.phaseMonthlyCost,
        avgDailyCost: phaseCalc.phaseCostPerDayNeeded,

        // Policy Benefit
        policyType: ltcPolicy.policyType,
        initialMaxBenefitAmount: ltcPolicy.policyMaximumBenefitAmount,
        policyContinuationBenefitAmount: ltcPolicy.policyContinuationBenefitAmount ?? null,
        inflationProtection: ltcPolicy.policyInflationProtection
          ? `${formatPercent(ltcPolicy.policyInflationProtection)} ${getInflationTypeLabel(ltcPolicy.isSimpleInflationProtection)}`
          : null,
        inflatedBenefitAmount:
          phaseCalc.inflatedMaxBenefitAmount > 0 ? phaseCalc.inflatedMaxBenefitAmount : null,
        policyBenefitAvailable: phaseCalc.remainingMaxBenefitBeforePhaseReduction,

        // Eligible Care Days
        adlClaimEligibilityRequired,
        estimatedDaysInPhase: phaseCalc.phaseCareDaysNeeded,
        eligibleDaysWithTwoAdls: adlClaimEligibilityRequired
          ? phaseCalc.daysWhereLessThanTwoADLs
          : null,
        waitingPeriod,
        eligibleDaysAfterWaiting: phaseCalc.phaseEligibleCareDaysAfterWaitingPeriod,

        // Daily Maximum Benefit
        dailyMaxBenefitForCare: phaseCalc.dailyBenefitAmount || null,
        inflatedDailyMaxBenefit:
          phaseCalc.inflatedDailyBenefitAmount > 0 ? phaseCalc.inflatedDailyBenefitAmount : null,
        expectedDailyPayout,
        isReimbursementPolicy: !ltcPolicy.isIndemnityPolicyPayment,

        // Cost Coverage
        remainingBenefitAmount: phaseCalc.remainingMaxBenefitBeforePhaseReduction,
        eligibleCareCost: phaseCalc.portionOfPhaseCostBenefitEligible,
        maxCoverageBasedOnDailyMax:
          phaseCalc.maxPayoutAccordingToDailyMaximum > 0
            ? phaseCalc.maxPayoutAccordingToDailyMaximum
            : null,
        totalCostCoverage: phaseCalc.costCoverageAddedToTotal,
        remainingBenefitAfterPhase: phaseCalc.remainingMaxBenefitAfterPhaseReduction,
      };
    });
}

/**
 * Read a field from `sourceQuote`, accepting either a parsed object or a
 * stringified JSON payload. Older persisted records can carry the latter,
 * so every consumer that touches `sourceQuote` needs to handle both shapes —
 * use this helper instead of inlining the parse.
 *
 * Returns `unknown`; callers narrow the result themselves.
 */
export function readSourceQuoteField(
  sourceQuote: PolicyFundingSource['sourceQuote'],
  key: string,
): unknown {
  if (!sourceQuote) return undefined;
  let quote: unknown = sourceQuote;
  if (typeof quote === 'string') {
    try {
      quote = JSON.parse(quote);
    } catch {
      return undefined;
    }
  }
  if (quote && typeof quote === 'object' && key in quote) {
    return (quote as Record<string, unknown>)[key];
  }
  return undefined;
}

export function buildPolicyInsightData(
  client: Client,
  phaseCalculations: SinglePhaseCalculations[],
  ltcPolicy: PolicyFundingSource,
): PolicyInsightData {
  const {
    policyPremiumMonthlyCost,
    policyPremiumStartYear,
    premiumInvestmentPeriodMonths,
    policyTotalCost,
    projectedCostCoverage,
    policyCoverageOfRemainingLtcCosts,
    policyLumpSumPayment,
    monthlyPremiumTotalCost,
    policyType,
    policyLimitedPayYears,
  } = ltcPolicy;

  // Match legacy logic: use projectedCostCoverage as primary, fallback to policyCoverageOfRemainingLtcCosts
  const totalPolicyCoverage = projectedCostCoverage ?? policyCoverageOfRemainingLtcCosts;

  const policyDurationMonths = premiumInvestmentPeriodMonths ?? 0;
  const policyDurationYears = Math.floor(policyDurationMonths / 12);

  // Determine if this is a lump-sum only policy (no monthly premiums)
  const hasLumpSum = policyLumpSumPayment !== null && policyLumpSumPayment > 0;
  const hasMonthlyPremium = policyPremiumMonthlyCost !== null && policyPremiumMonthlyCost > 0;
  const isLumpSumOnly = hasLumpSum && !hasMonthlyPremium;

  // Decide whether to present this policy in annual terms. Two cases:
  //   1. The source quote was billed annually (e.g. CareScout's annual mode) —
  //      the carrier's actual bill is yearly, so showing monthly is misleading.
  //   2. The policy is a limited-pay product (5-pay, 10-pay, 20-pay, etc.) —
  //      advisors and clients conventionally discuss these in annual terms
  //      ("$5,000/year for 10 years") even when the carrier bills monthly.
  // Both cases use the same display: "Annual Premium $X (≈ $Y/mo)".
  const sourceFrequency = readSourceQuoteField(ltcPolicy.sourceQuote, 'premiumFrequency');
  const sourceModalPremium = readSourceQuoteField(ltcPolicy.sourceQuote, 'primaryModalPremium');
  const hasAnnualSourceQuote =
    !isMembershipProgram(policyType) && sourceFrequency === 'Annually';
  const isLimitedPay =
    !isMembershipProgram(policyType) && (policyLimitedPayYears ?? 0) > 0;
  const isAnnualBilled = hasAnnualSourceQuote || isLimitedPay;
  const computedAnnual = (policyPremiumMonthlyCost ?? 0) * 12;
  // Prefer the carrier's exact quoted annual when the source quote was annual
  // and the stored monthly is unedited (within $1). Limited-pay policies
  // typically have monthly source quotes, so the carrier-exact value isn't
  // applicable — fall back to monthly × 12.
  const annualPremium = isAnnualBilled
    ? hasAnnualSourceQuote &&
      typeof sourceModalPremium === 'number' &&
      Math.abs(sourceModalPremium - computedAnnual) < 1
      ? sourceModalPremium
      : computedAnnual
    : null;

  // Get phase coverages
  const earlyCareCalc = phaseCalculations[0];
  const moderateCareCalc = phaseCalculations[1];
  const fullCareCalc = phaseCalculations[2];

  return {
    // Total Invested Into Policy
    monthlyPremium: policyPremiumMonthlyCost,
    isAnnualBilled,
    annualPremium,
    policyDuration: `${policyDurationMonths} months${policyPremiumStartYear ? ` (${policyPremiumStartYear}-${policyPremiumStartYear + policyDurationYears})` : ''}`,
    monthlyPremiumTotalCost: monthlyPremiumTotalCost ?? null,
    lumpSumPayment: hasLumpSum ? policyLumpSumPayment : null,
    predictedTotalPremiumsPaid: policyTotalCost ?? null,
    isLumpSumOnly,

    // Long-Term Care Payout Summary
    earlyCarePhase: earlyCareCalc?.costCoverageAddedToTotal ?? null,
    moderateCarePhase: moderateCareCalc?.costCoverageAddedToTotal ?? null,
    fullCarePhase: fullCareCalc?.costCoverageAddedToTotal ?? null,
    totalPolicyCoverage: totalPolicyCoverage ?? null,

    // Phase data
    phases: buildPhasePayoutData(client, phaseCalculations, ltcPolicy),
  };
}

export function formatCurrencyValue(value?: number | null, decimals = 2): string {
  return formatCurrency(value ?? 0, decimals);
}

export function getInflationTypeLabel(isSimple: boolean): string {
  return isSimple ? 'simple' : 'compound';
}

export function getCareEnvironmentLabel(careEnvironment: CareEnvironment): string {
  switch (careEnvironment) {
    case CareEnvironment.home:
      return 'at home';
    case CareEnvironment.assistedLiving:
      return 'at an assisted living facility';
    case CareEnvironment.fullCareFacility:
      return 'at a full-care facility';
    case CareEnvironment.independentLiving:
      return 'at an independent living facility';
    default:
      return '';
  }
}
